import { getThingsByThingType } from './apis';
import React from 'react';
import { CSVLink } from 'react-csv';
import { Header } from '@amzn/awsui-components-console';
import {
    COLUMN_DEFINITIONS,
    FILTER_SETS,
    HOSTS_THING_TYPE,
    DEVICES_THING_TYPE,
    EXPORTED_CSV_HEADERS,
    HOSTS,
    DEVICES,
} from './constants';

export function getFilterSet(filterSetName, tableData) {
    // deep clone the object
    let filters = JSON.parse(JSON.stringify(FILTER_SETS[filterSetName]));
    let additionalFilters = {};

    tableData.forEach((row) => {
        for (const cell in row) {
            if (filters[cell] && row[cell]) {
                if (!additionalFilters[cell]) additionalFilters[cell] = new Set();
                additionalFilters[cell].add(row[cell]);
            }
        }
    });

    for (const item in additionalFilters) {
        const newOptions = Array.from(additionalFilters[item])
            .sort()
            .map((e) => {
                return {
                    value: e,
                    lalbel: e,
                };
            });
        filters[item].options.push(...newOptions);
    }

    return filters;
}

export async function filterData(tableData, filterSet, customRowFilter = null) {
    const result = tableData.filter((row) => {
        for (const cell in row) {
            if (
                filterSet[cell] &&
                filterSet[cell].selectedOption.value != 'all' &&
                filterSet[cell].selectedOption.value != row[cell]
            ) {
                return false;
            }
        }

        return customRowFilter ? customRowFilter(row) : true;
    });
    return result;
}

/**
 * Module to query the metrics by thing type and region
 * @param thingType Thing type selected by user
 * @param selectedRegion Region selected by user
 * @returns {Promise<*>}
 */
export async function queryMetrics(thingType, selectedRegion) {
    if (thingType === HOSTS_THING_TYPE) {
        return await getHosts(selectedRegion);
    }
    if (thingType === DEVICES_THING_TYPE) {
        return await getDevices(selectedRegion);
    }
}

/**
 * Function to list all the hosts on-boarded to farm
 * @param selectedRegion Region selected by user
 * @returns {Promise<*>}
 */
export async function getHosts(selectedRegion) {
    const response = await getThingsByThingType(HOSTS_THING_TYPE, selectedRegion);
    const hosts = response.response.map((thing) => {
        return {
            labID: thing.attributes.labID,
            hostName: thing.thingName,
            hostRegion: thing.attributes.hostRegion,
            activateStatus: thing.attributes.activateStatus,
            connectionStatus: thing.attributes.connectionStatus,
        };
    });
    return hosts;
}

/**
 * Function to list all the devices on-boarded to farm
 * @param selectedRegion Region selected by user
 * @returns {Promise<*>}
 */
export async function getDevices(selectedRegion) {
    const response = await getThingsByThingType(DEVICES_THING_TYPE, selectedRegion);
    const devices = response.response.map((thing) => {
        return {
            name: thing.attributes.deviceName,
            host: thing.attributes.deviceHost,
            dsn: thing.thingName,
            status: thing.attributes.deviceStatus,
            buildProject: thing.attributes.deviceBuildProject,
            buildType: thing.attributes.deviceBuildType,
            buildVersion: thing.attributes.deviceBuildVersion,
            buildVariant: thing.attributes.deviceBuildVariant,
            connectStatus: thing.attributes.deviceConnectStatus,
            family: thing.attributes.deviceFamily,
            locale: thing.attributes.deviceLocale,
            model: thing.attributes.deviceModel,
            storage: formatStorageUnit(thing.attributes.deviceStorage),
            user: thing.attributes.deviceUser,
            wifiStatus: thing.attributes.deviceWIFIStatus,
        };
    });
    return devices;
}

export function getColumnDefinitions(thingType) {
    return COLUMN_DEFINITIONS[thingType] || [];
}

/**
 * format storage unit
 * @param  {int} kilobytes amount of storage in kilobytes
 * @return {String}  formated storage with unit (i.e: 1 MB)
 */
export function formatStorageUnit(kilobytes) {
    if (isNaN(kilobytes) || kilobytes <= 0) {
        return kilobytes;
    }

    const units = ['KB', 'MB', 'GB', 'TB'];
    const index = Math.min(Math.floor(Math.log10(kilobytes) / Math.log10(1024)), units.length - 1);

    const unit = units[index];
    const number = Math.floor(kilobytes / Math.pow(1024, index));

    return `${number} ${unit}`;
}

/**
 * get table header
 * @param thingType: DFEdges / DFDevices
 * @param displayData: the display data
 * @return {String} table header with the number of displayed data, with a download button
 */
export function getHeader(thingType, displayData) {
    const header = thingType === HOSTS_THING_TYPE ? HOSTS : DEVICES;
    const fileName = header + '.csv';
    return (
        <Header
            counter={displayData.length ? `(${displayData.length})` : `(0)`}
            actions={
                <CSVLink data={displayData} headers={EXPORTED_CSV_HEADERS[thingType]} filename={fileName}>
                    Export as csv
                </CSVLink>
            }
        >
            {header}
        </Header>
    );
}
